// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Loader2, Save, Shield } from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { tenantSettingsService } from "../../services/tenantSettingsService";
import { usePermissions } from "../../hooks/usePermissions";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess, hasAdminAccess } from "../../config/roleConfig";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import useTenants from "../../hooks/useTenants";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { emitTenantSettingsUpdated } from "../../utils/tenantSettingsEvents";
import PageLoader from "../../components/PageLoader";

const LokalisierungSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const { userRoles, userInfo } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);
  const isAdmin = hasAdminAccess(userRoles || []);
  let selectednewTenant = null;
  if (isAdmin) {
    selectednewTenant = userInfo.tenant_id || null;
  }
  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
  } = useTenants({ ...getAdminTenantConfig(isSuperAdmin), includeAll: false });

  const loadSettings = async () => {
    try {
      setLoading(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        setSettings({});
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        setSettings({});
        return;
      }

      const config = await tenantSettingsService.getTenantConfig(tenantId);
      setSettings(config.localization || {});
    } catch (error) {
      console.error("Error loading localization settings:", error);
      showError("Fehler beim Laden der Lokalisierungseinstellungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [selectedTenant, isSuperAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        showError("Bitte zuerst einen Mandanten auswählen");
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        showError("Kein Mandant für das Speichern gefunden");
        return;
      }

      // Merge with existing config so we don't overwrite other sections
      const currentConfig =
        await tenantSettingsService.getTenantConfig(tenantId);
      const nextConfig = {
        ...currentConfig,
        localization: settings,
      };

      await tenantSettingsService.updateTenantConfig(tenantId, nextConfig);
      emitTenantSettingsUpdated(tenantId);
      showSuccess("Lokalisierungseinstellungen erfolgreich gespeichert!");
      await loadSettings();
    } catch (error) {
      console.error("Error saving localization settings:", error);
      showError("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  if (
    !isSuperAdmin &&
    !isAdmin &&
    !hasPermission("tenant_settings.manage") &&
    !hasPermission("tenant_settings.view")
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-slate-400">
            Keine Berechtigung für diese Einstellungen
          </p>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:placeholder-slate-400";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Lokalisierung"
        subtitle="Sprache, Zeitzone und Formate"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
      />
      <div className="mx-auto px-4 lg:px-6 sm:mt-6 mt-3 pb-8">
        {loading ? (
          <PageLoader message="Lade Einstellungen..." />
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 sm:p-6 p-4 w-full">
            <form className="space-y-3" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Standardsprache
                  </label>
                  <select
                    name="default_language"
                    value={settings.default_language || "en"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="de">Deutsch</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="it">Italiano</option>
                    <option value="pl">Polski</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Zeitzone
                  </label>
                  <select
                    name="timezone"
                    value={settings.timezone || "UTC"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Europe/Berlin">Europe/Berlin</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Paris">Europe/Paris</option>
                  </select>
                </div>

                <div className="hidden">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Datumsformat
                  </label>
                  <select
                    name="date_format"
                    value={settings.date_format || "MM/DD/YYYY"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div className="hidden">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Zeitformat
                  </label>
                  <select
                    name="time_format"
                    value={settings.time_format || "24h"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="24h">24 Stunden</option>
                    <option value="12h">12 Stunden</option>
                  </select>
                </div>

                <div className="hidden">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Währung
                  </label>
                  <select
                    name="currency"
                    value={settings.currency || "EUR"}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CHF">CHF (CHF)</option>
                  </select>
                </div>
                <div className="">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Unterstützte Sprachen
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 bg-primary-color-tint primary-color font-medium">
                      Deutsch
                    </span>
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 bg-primary-color-tint primary-color font-medium">
                      English
                    </span>
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200">
                      Français
                    </span>
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200">
                      Español
                    </span>
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200">
                      Italiano
                    </span>
                    <span className="px-4 py-2 rounded-xl text-sm font-medium transition-all text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200">
                      Polski
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => loadSettings()}
                  className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-primary-color dark:bg-primary-color/40 text-white rounded-lg hover:bg-primary-color-dark dark:hover:bg-primary-color/40 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {saving ? "Speichern..." : "Einstellungen speichern"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default LokalisierungSettings;
